import React, { useRef, useState, useLayoutEffect } from 'react';
import { Button, ButtonProps, Dropdown, DropdownItemProps, Popup } from 'semantic-ui-react';
import { canIUse, AppFeatures, Features } from '../../helpers/PermissionUtility';
import { getStatus } from '../helpers';
import { formActionMenuItemTooltips, TemplateActionTypes } from '../../helpers/constants';
import PublicShareFormHeader from './PublicShareHeader';
import RenamableHeader from '../../components/RenamableHeader';
import { GoFormzLogoIcon } from '../../icons/icons';
import '../styles/FormEditor.scss';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';
import InvalidPublicShare from '../../publicshare/InvalidPublicShare';
import { useAppSelector } from '../../hooks/ReduxHooks';
import { User } from '../../usersgroups/usergroup-types';
import { FormEditorView } from '../formeditor-types';
import RequiredFieldDropdown from './RequiredFieldDropdown';
import { Form } from '../../types/form-types';
import { DataLayout, DataLayoutPosition, DataLayoutTab, FieldPosition, Template, TemplateAction, TemplatePage } from '../../types/template-types';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { ErrorType } from '../../templateeditor/components/FieldValidations/constants';
import ValidationErrorsPopup from './FieldValidations/ValidationErrorsPopup';

export type DropDownClickEventHandler = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, data: DropdownItemProps) => void;

export interface HeaderProps {
  formName?: string;
  form?: Form;
  userMap?: Record<string, unknown>;
  isUnsaved?: boolean;
  isNewForm?: boolean;
  user?: User;
  userName?: string;
  template?: Template;
  completedDate?: string;
  completeInProgress?: boolean;
  isFetching?: boolean;
  isSaving?: boolean;
  sharedAccessToken?: string;
  publicShareInvalid?: string;
  openTemplateListModal?: VoidFunc;
  clickCustomAction?: (action: TemplateAction) => void;
  reopenForm?: VoidFunc;
  duplicateForm?: (navigate: NavigateFunction) => Promise<void>;
  openFormHistoryModal?: (formName: string, formId: string) => void;
  openChangeTemplateVersionModal?: DropDownClickEventHandler;
  deleteForm?: DropDownClickEventHandler;
  completeForm?: VoidFunc;
  saveForm?: VoidFunc;
  allowFormRevert?: boolean;
  onRevertToVersion?: (formId: string, versionId: string) => void;
  formEditorUpdateName?: (name: string, manualNameOverride: boolean, autoGenerated: boolean) => void;
  initialCalculationsHaveRun?: boolean;
  confirmSubmitSharedForm?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, data: ButtonProps) => void;
  openTakeOwnershipModal?: VoidFunc;
  getStatusIcon?: (icon: string) => unknown;
  view: FormEditorView;
  setView: (view: FormEditorView) => void;
  previewMode?: boolean;
  versionId?: string;
  openTransferModal?: DropDownClickEventHandler;
  disableTransferButton?: boolean;
  openTagsModal?: DropDownClickEventHandler;
  isFormOpen?: boolean;
  openPdfModal?: DropDownClickEventHandler;
  formStatus?: string;
  openPublicFormsModal?: DropDownClickEventHandler;
  pages: TemplatePage[];
  dataLayouts?: DataLayout[];
  formId?: string;
  bringToFieldPosition: (page: TemplatePage, position: FieldPosition) => void;
  bringToDataLayoutPosition: (tab: DataLayoutTab, position: DataLayoutPosition) => void;
  previewView: string;
  setPreviewView: (view) => void;
  savePublicForm: VoidFunc;
}

export default function Header(props: HeaderProps) {
  const {
    allowFormRevert,
    completedDate,
    completeInProgress,
    dataLayouts,
    disableTransferButton,
    form,
    formId,
    formName,
    initialCalculationsHaveRun,
    isFetching,
    isFormOpen,
    isNewForm,
    isSaving,
    isUnsaved,
    pages,
    publicShareInvalid,
    previewMode,
    previewView,
    sharedAccessToken,
    template,
    user,
    userMap,
    versionId,
    view,
    bringToDataLayoutPosition,
    bringToFieldPosition,
    clickCustomAction,
    completeForm,
    confirmSubmitSharedForm,
    deleteForm,
    duplicateForm,
    formEditorUpdateName,
    getStatusIcon,
    openChangeTemplateVersionModal,
    openFormHistoryModal,
    openPdfModal,
    openPublicFormsModal,
    openTagsModal,
    openTakeOwnershipModal,
    openTransferModal,
    onRevertToVersion,
    reopenForm,
    saveForm,
    setPreviewView,
    setView
  } = props;

  const headerRef = useRef<HTMLDivElement>();
  const rightMenuRef = useRef<HTMLDivElement>();
  const centerMenuRef = useRef<HTMLDivElement>();
  const isAdmin = useAppSelector<boolean>((state) => state.profile.isAdmin);
  const formEditorState = useAppSelector((state) => state.formeditor);
  const hasMissingRequiredFields = formEditorState.missingRequiredFieldIds?.length > 0;
  const invalidFieldValues = formEditorState.invalidFieldValues;
  const hasErrorFieldValues = invalidFieldValues?.filter((x) => x.errorSettings.errorType === ErrorType.Error).length > 0;

  const [resize, setResize] = useState<number>();
  const [isOpen, setMenuItemOpen] = useState(false);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const updateDimensions = () => {
      const headerDiv = headerRef.current ? headerRef.current.clientWidth - 50 : null;
      const rightMenuDiv = rightMenuRef.current ? rightMenuRef.current.clientWidth : null;
      const centerMenuDiv = centerMenuRef.current ? centerMenuRef.current.clientWidth : null;
      const formNameWrapper = headerDiv - rightMenuDiv - centerMenuDiv;

      if (previewMode && headerDiv > 968) {
        const centeredHeaderWidth = headerDiv / 2 - 100;
        setResize(centeredHeaderWidth);
      } else {
        setResize(formNameWrapper);
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions, { capture: true });
    return () => {
      window.removeEventListener('resize', updateDimensions, { capture: true });
    };
  });

  function getSwitchViewDropdownItem() {
    if (pages.length === 0 || dataLayouts.length === 0) {
      if (previewMode) {
        return <Dropdown.Item disabled={true} text={view === 'form' ? 'Switch to List View' : 'Switch to Form View'} />;
      }

      return null;
    }

    return (
      <Dropdown.Item
        text={view === 'form' ? 'Switch to List View' : 'Switch to Form View'}
        onClick={() => {
          if (view === 'form') {
            setView('list');
            analyticsTrack('Form Editor - Use List View Selected');
          } else {
            setView('form');
            analyticsTrack('Form Editor - Use Form View Selected');
          }
        }}
      />
    );
  }

  function handleClosePreviewClick() {
    if (previewMode) {
      analyticsTrack('Preview - Closed', {});
      window.close();
    }
  }

  const handlePopupOpen = () => {
    // this prop can undefined so we need to use !! to convert it to a boolean
    setMenuItemOpen(!!disableTransferButton);
  };

  const handlePopupClose = () => {
    setMenuItemOpen(false);
  };

  function openLatestFormVersion() {
    const url = `/forms/${formId}`;
    window.open(url);
  }

  const status = form ? getStatus(user, form, userMap, isUnsaved) : undefined;
  const isOwned = form && form.userId === user.id;

  let leftMenu = null;
  let centerMenu = null;
  let rightMenu = null;

  if (publicShareInvalid && !form && sharedAccessToken) {
    return <InvalidPublicShare errorMessage={publicShareInvalid} />;
  }

  leftMenu = (
    <div className="item">
      {!isFetching ? (
        <div className="header-status-wrapper" style={{ maxWidth: resize, minWidth: '200px' }}>
          <RenamableHeader
            name={formName}
            previewMode={previewMode}
            updateName={(name) => formEditorUpdateName(name, true, false)}
            readOnly={!(form && !previewMode && !completedDate && canIUse(AppFeatures.FormRename) && !versionId)}
            maxLength={200}
          />
          {!previewMode ? (
            <div className="header-status">
              {status ? getStatusIcon(status.Icon) : ''}
              <span className="header-status-italics" style={{ marginLeft: '5px' }}>
                {status ? status.Status : ''}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  centerMenu = previewMode ? (
    <div className="center" ref={centerMenuRef}>
      <div className={classNames('item', 'mobile-hide')} onClick={() => setPreviewView('desktop')}>
        <div className={classNames('preview-icon', previewView == 'desktop' && ' selected')}>{Desktop}</div>
        <span className={classNames('title', previewView == 'desktop' && ' selected')}>Desktop</span>
      </div>
      <div className={classNames('item', 'mobile-hide')} onClick={() => setPreviewView('tablet')}>
        <div className={classNames('preview-icon', previewView == 'tablet' && ' selected')}>{Tablet}</div>
        <span className={classNames('title', previewView == 'tablet' && ' selected')}>Tablet</span>
      </div>
      <div className={classNames('item', 'mobile-hide')} onClick={() => setPreviewView('phone')}>
        <div className={classNames('preview-icon', previewView == 'phone' && ' selected')}>{Phone}</div>
        <span className={classNames('title', previewView == 'phone' && ' selected')}>Phone</span>
      </div>
    </div>
  ) : (
    <div ref={centerMenuRef}></div>
  );

  const completeButtonText = previewMode ? 'Test Complete' : 'Complete';

  const customActionMenuItems =
    !versionId && template && !completedDate && template.actions && template.actions.length > 0
      ? template.actions
          .filter(
            (action) =>
              action.actionType === (!previewMode ? TemplateActionTypes.WorkflowTrigger : null) ||
              action.actionType === TemplateActionTypes.WebView ||
              (action.actionType === TemplateActionTypes.DeepLink && action.webUrl)
          )
          .map((action) => <Dropdown.Item key={action.id} text={action.name} onClick={() => clickCustomAction(action)} />)
      : null;

  rightMenu = (
    <div className="right menu" ref={rightMenuRef}>
      {previewMode && (
        <Dropdown
          selection
          options={deviceTypes}
          value={previewView}
          className="device-view-dropdown"
          onChange={(e, data) => setPreviewView(data.value)}
        ></Dropdown>
      )}
      {canIUse(Features.FieldValidations) && (hasMissingRequiredFields || invalidFieldValues?.length > 0) ? (
        <ValidationErrorsPopup bringToFieldPosition={bringToFieldPosition} bringToDataLayoutPosition={bringToDataLayoutPosition} />
      ) : (
        <RequiredFieldDropdown bringToFieldPosition={bringToFieldPosition} bringToDataLayoutPosition={bringToDataLayoutPosition} />
      )}
      <div className="item form-action-item">
        <Dropdown text="Form Actions" button className="tertiary form-action-button">
          <Dropdown.Menu>
            {form && !versionId && (!completedDate || completeInProgress) && canIUse(AppFeatures.FormComplete) && isOwned ? (
              <React.Fragment>
                <Dropdown.Item text={completeButtonText} onClick={completeForm} className="mobile-show" disabled={hasMissingRequiredFields} />
                <Dropdown.Divider className="mobile-show" />
              </React.Fragment>
            ) : null}
            {form && !isOwned && !versionId && !isFetching && canIUse(AppFeatures.TakeOwnership) && (completedDate ? canIUse(AppFeatures.FormReopen) : true) ? (
              <React.Fragment>
                <Dropdown.Item content="Take Ownership" onClick={openTakeOwnershipModal} className="mobile-show" />
                <Dropdown.Divider className="mobile-show" />
              </React.Fragment>
            ) : null}
            {form ? <React.Fragment>{getSwitchViewDropdownItem()}</React.Fragment> : null}
            {form && canIUse(AppFeatures.FormPDF) && !previewMode ? <Dropdown.Item text="Share PDF" onClick={!isFormOpen ? undefined : openPdfModal} /> : null}
            {form && completedDate && canIUse(AppFeatures.FormReopen) && !versionId && !previewMode ? (
              <Dropdown.Item text="Reopen" onClick={() => reopenForm()} />
            ) : null}
            {form && canIUse(AppFeatures.FormTransfer) && !previewMode && !versionId ? (
              <Popup
                content={formActionMenuItemTooltips.transfer}
                trigger={
                  <Dropdown.Item
                    className="form-action-item"
                    disabled={disableTransferButton}
                    text="Transfer"
                    onClick={disableTransferButton ? undefined : openTransferModal}
                  />
                }
                position="right center"
                on="hover"
                open={isOpen}
                onOpen={handlePopupOpen}
                onClose={handlePopupClose}
              />
            ) : null}
            {form && canIUse(AppFeatures.TagModal) && !previewMode && !versionId ? (
              <Dropdown.Item text="Tags" onClick={!isFormOpen ? undefined : openTagsModal} disabled={!isFormOpen} />
            ) : null}
            {form && !previewMode ? <Dropdown.Divider /> : null}
            {customActionMenuItems && customActionMenuItems.length > 0 ? (
              <React.Fragment>
                {customActionMenuItems}
                {!previewMode && !isNewForm ? <Dropdown.Divider /> : null}
              </React.Fragment>
            ) : null}
            {form && !isNewForm && canIUse(AppFeatures.PublicShare) && !previewMode && !versionId ? (
              <Dropdown.Item
                text={canIUse(Features.PublicFormV2) ? 'Send Form' : 'Create Public Form'}
                onClick={!isFormOpen ? undefined : openPublicFormsModal}
                disabled={!isFormOpen}
              />
            ) : null}
            {form && !isNewForm && !previewMode ? (
              <Dropdown.Item
                text="History"
                onClick={() => {
                  openFormHistoryModal(formName, formId);
                }}
              />
            ) : null}
            {form && !isNewForm && canIUse(AppFeatures.FormCopy) && !isUnsaved && !versionId && !previewMode ? (
              <Dropdown.Item text="Duplicate" onClick={() => duplicateForm(navigate)} />
            ) : null}
            {form && !isNewForm && canIUse(AppFeatures.Workflow) ? (
              <Dropdown.Item
                text="Workflow jobs"
                onClick={() => {
                  navigate(`/workflows/jobs?resourceId=${formId}`);
                }}
              />
            ) : null}
            {form && !isNewForm && !completedDate && isOwned && isAdmin && form.templateVersionId && !versionId ? (
              <Dropdown.Item text="Template version" onClick={openChangeTemplateVersionModal} />
            ) : null}
            {versionId ? <Dropdown.Item text={`Open latest Form version`} onClick={openLatestFormVersion} /> : null}
            {allowFormRevert && canIUse(AppFeatures.FormRevert) ? (
              <Dropdown.Item text={`Revert to this version`} onClick={() => onRevertToVersion(formId, versionId)} />
            ) : null}
            {form &&
            !isNewForm &&
            (canIUse(AppFeatures.FormDelete) || (canIUse(AppFeatures.FormDeleteDrafts) && !completedDate)) &&
            !versionId &&
            !previewMode ? (
              <Dropdown.Item text="Delete" onClick={deleteForm} />
            ) : null}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {form && !isOwned && !versionId && !isFetching && canIUse(AppFeatures.TakeOwnership) && (completedDate ? canIUse(AppFeatures.FormReopen) : true) ? (
        <div className="item take-ownership">
          <Button content="Take Ownership" onClick={openTakeOwnershipModal} />
        </div>
      ) : null}
      {form && !versionId && (!completedDate || completeInProgress) && canIUse(AppFeatures.FormComplete) && isOwned ? (
        <div className="item form-complete">
          <Button
            content={completeButtonText}
            loading={completeInProgress}
            onClick={completeForm}
            disabled={isSaving || isFetching || hasMissingRequiredFields || hasErrorFieldValues}
          />
        </div>
      ) : null}
      {form && !previewMode && !versionId && !completedDate && isOwned ? (
        <div className="item">
          <Button className="primary" content="Save" loading={isSaving} disabled={isSaving || isFetching || !isUnsaved} onClick={saveForm} />
        </div>
      ) : null}
      {previewMode && (
        <div className="item close-preview">
          <Button primary content="Close" onClick={handleClosePreviewClick} />
        </div>
      )}
    </div>
  );
  return (
    <div className="header-wrapper">
      <div className={`${sharedAccessToken ? 'logo' : 'hide-logo'}`}>
        <GoFormzLogoIcon />
      </div>
      <div className="header" ref={headerRef}>
        {!sharedAccessToken ? (
          <div className="ui secondary fitted menu">
            {leftMenu}
            {centerMenu}
            {!isFetching ? rightMenu : null}
          </div>
        ) : (
          <PublicShareFormHeader
            isSaving={isSaving}
            confirmSubmitSharedForm={confirmSubmitSharedForm}
            isFetching={isFetching}
            initialCalculationsHaveRun={initialCalculationsHaveRun}
            bringToDataLayoutPosition={bringToDataLayoutPosition}
            bringToFieldPosition={bringToFieldPosition}
            // only supporting save on public forms using the new short shared access token style
            allowSave={sharedAccessToken.length <= 20 && canIUse(Features.SavePublicForms)}
            sharedAccessToken={sharedAccessToken}
            savePublicForm={props.savePublicForm}
          />
        )}
      </div>
    </div>
  );
}

const Desktop = (
  <svg width="36" height="30" viewBox="0 0 36 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="35" height="22.4375" rx="1.5" stroke="#333333" />
    <rect x="3.2002" y="3.3125" width="29.6" height="16.8125" rx="0.5" stroke="#333333" />
    <path
      d="M17.0996 22.5H18.8996V24.4125C18.8996 24.9096 18.4967 25.3125 17.9996 25.3125V25.3125C17.5026 25.3125 17.0996 24.9096 17.0996 24.4125V22.5Z"
      fill="#333333"
    />
    <path
      d="M12.7998 26.75H23.1998C24.0282 26.75 24.6998 27.4216 24.6998 28.25V29.5H11.2998V28.25C11.2998 27.4216 11.9714 26.75 12.7998 26.75Z"
      stroke="black"
    />
  </svg>
);

const Tablet = (
  <svg width="21" height="26" viewBox="0 0 21 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.0456 23.5L10.0449 23.5C9.94603 23.5 9.84937 23.4707 9.76714 23.4157C9.68491 23.3608 9.62082 23.2827 9.58298 23.1913C9.54514 23.1 9.53524 22.9994 9.55453 22.9025C9.57382 22.8055 9.62144 22.7164 9.69137 22.6464C9.76129 22.5765 9.85038 22.5289 9.94738 22.5096C10.0444 22.4903 10.1449 22.5002 10.2363 22.5381C10.3276 22.5759 10.4057 22.64 10.4607 22.7222C10.5156 22.8044 10.5449 22.9011 10.5449 23C10.5449 23.0657 10.532 23.1308 10.5068 23.1915C10.4816 23.2522 10.4447 23.3074 10.3982 23.3538C10.3517 23.4002 10.2965 23.437 10.2358 23.4621C10.1744 23.4875 10.1106 23.5001 10.0456 23.5Z"
      fill="#333333"
      stroke="#333333"
    />
    <rect x="0.544922" y="0.5" width="19" height="25" rx="1.5" stroke="#333333" />
    <rect x="2.54492" y="2.5" width="15" height="18" stroke="#333333" />
  </svg>
);

const Phone = (
  <svg width="14" height="23" viewBox="0 0 14 23" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="path-1-inside-1_301_1033" fill="white">
      <path d="M6.54492 21C6.44603 21 6.34936 20.9707 6.26714 20.9157C6.18491 20.8608 6.12083 20.7827 6.08298 20.6913C6.04514 20.6 6.03524 20.4994 6.05453 20.4025C6.07382 20.3055 6.12144 20.2164 6.19137 20.1464C6.26129 20.0765 6.35039 20.0289 6.44738 20.0096C6.54437 19.9903 6.6449 20.0002 6.73626 20.0381C6.82763 20.0759 6.90572 20.14 6.96066 20.2222C7.0156 20.3044 7.04492 20.4011 7.04492 20.5C7.04492 20.5657 7.03197 20.6308 7.0068 20.6915C6.98163 20.7522 6.94474 20.8074 6.89824 20.8538C6.85174 20.9002 6.79654 20.937 6.7358 20.9621C6.67506 20.9872 6.61064 21.0001 6.54492 21Z" />
    </mask>
    <path
      d="M6.54492 21C6.44603 21 6.34936 20.9707 6.26714 20.9157C6.18491 20.8608 6.12083 20.7827 6.08298 20.6913C6.04514 20.6 6.03524 20.4994 6.05453 20.4025C6.07382 20.3055 6.12144 20.2164 6.19137 20.1464C6.26129 20.0765 6.35039 20.0289 6.44738 20.0096C6.54437 19.9903 6.6449 20.0002 6.73626 20.0381C6.82763 20.0759 6.90572 20.14 6.96066 20.2222C7.0156 20.3044 7.04492 20.4011 7.04492 20.5C7.04492 20.5657 7.03197 20.6308 7.0068 20.6915C6.98163 20.7522 6.94474 20.8074 6.89824 20.8538C6.85174 20.9002 6.79654 20.937 6.7358 20.9621C6.67506 20.9872 6.61064 21.0001 6.54492 21Z"
      fill="#333333"
    />
    <path
      d="M6.54492 21L6.54626 20L6.54492 20L6.54492 21ZM7.04492 20.5L6.04492 20.5L6.04492 20.5L7.04492 20.5ZM6.54492 20C6.64382 20 6.74049 20.0293 6.82271 20.0843L5.71157 21.7472C5.95824 21.912 6.24825 22 6.54492 22L6.54492 20ZM6.82271 20.0843C6.90493 20.1392 6.96902 20.2173 7.00686 20.3087L5.1591 21.074C5.27264 21.3481 5.4649 21.5824 5.71157 21.7472L6.82271 20.0843ZM7.00686 20.3087C7.04471 20.4 7.05461 20.5006 7.03531 20.5975L5.07374 20.2074C5.01587 20.4983 5.04557 20.7999 5.1591 21.074L7.00686 20.3087ZM7.03531 20.5975C7.01602 20.6945 6.9684 20.7836 6.89847 20.8536L5.48426 19.4393C5.27448 19.6491 5.13162 19.9164 5.07374 20.2074L7.03531 20.5975ZM6.89847 20.8536C6.82854 20.9235 6.73945 20.9711 6.64247 20.9904L6.25229 19.0288C5.96133 19.0867 5.69405 19.2296 5.48426 19.4393L6.89847 20.8536ZM6.64247 20.9904C6.54548 21.0097 6.44495 20.9998 6.35358 20.9619L7.11895 19.1142C6.84486 19.0006 6.54326 18.9709 6.25229 19.0288L6.64247 20.9904ZM6.35358 20.9619C6.26222 20.9241 6.18413 20.86 6.12919 20.7778L7.79213 19.6666C7.6273 19.42 7.39304 19.2277 7.11895 19.1142L6.35358 20.9619ZM6.12919 20.7778C6.07424 20.6956 6.04492 20.5989 6.04492 20.5L8.04492 20.5C8.04492 20.2033 7.95695 19.9133 7.79213 19.6666L6.12919 20.7778ZM6.04492 20.5C6.04492 20.4343 6.05788 20.3692 6.08305 20.3085L7.93055 21.0745C8.00606 20.8924 8.04492 20.6972 8.04492 20.5L6.04492 20.5ZM6.08305 20.3085C6.10821 20.2478 6.14509 20.1927 6.19161 20.1462L7.60488 21.5614C7.74439 21.422 7.85505 21.2566 7.93055 21.0745L6.08305 20.3085ZM6.19161 20.1462C6.23811 20.0998 6.29331 20.063 6.35404 20.0379L7.11756 21.8864C7.29977 21.8111 7.46537 21.7007 7.60488 21.5614L6.19161 20.1462ZM6.35404 20.0379C6.41353 20.0133 6.47919 19.9999 6.54626 20L6.54359 22C6.74208 22.0003 6.93659 21.9611 7.11756 21.8864L6.35404 20.0379Z"
      fill="#333333"
      mask="url(#path-1-inside-1_301_1033)"
    />
    <rect x="0.544922" y="0.5" width="12" height="22" rx="1.5" stroke="#333333" />
    <rect x="2.54492" y="4.5" width="8" height="14" stroke="#333333" />
    <rect x="5.04492" y="2" width="3" height="1" rx="0.5" fill="#333333" />
  </svg>
);

const deviceTypes = [
  {
    key: 'Desktop',
    text: 'Desktop',
    value: 'desktop'
  },
  {
    key: 'Tablet',
    text: 'Tablet',
    value: 'tablet'
  },
  {
    key: 'Phone',
    text: 'Phone',
    value: 'phone'
  }
];
